/**
 * Issue #187 — workshop UI + AI response language (fi | en | sv).
 * Shared by backend, rag-service, and display app (normalize only on client).
 */

export const SUPPORTED_INSTANCE_LANGUAGES = ['fi', 'en', 'sv'];

/**
 * @param {unknown} code
 * @returns {'fi'|'en'|'sv'}
 */
export function normalizeInstanceLanguage(code) {
  if (code == null || code === '') return 'fi';
  const c = String(code).trim().toLowerCase();
  if (c === 'en' || c === 'eng' || c === 'english') return 'en';
  if (c === 'sv' || c === 'se' || c === 'swe' || c === 'swedish') return 'sv';
  if (c === 'fi' || c === 'fin' || c === 'finnish') return 'fi';
  return 'fi';
}

/**
 * @param {object | null | undefined} row — kuutio_instances row (language column + optional settings jsonb)
 * @returns {'fi'|'en'|'sv'}
 */
export function getInstanceLanguageFromRow(row) {
  if (!row || typeof row !== 'object') return 'fi';
  if (row.language != null && String(row.language).trim() !== '') {
    return normalizeInstanceLanguage(row.language);
  }
  const s = row.settings;
  if (s && typeof s === 'object' && s.language != null) {
    return normalizeInstanceLanguage(s.language);
  }
  if (typeof s === 'string') {
    try {
      const parsed = JSON.parse(s);
      if (parsed?.language != null) return normalizeInstanceLanguage(parsed.language);
    } catch {
      /* ignore */
    }
  }
  return 'fi';
}

/**
 * @param {import('pg').Pool | null | undefined} pool
 * @param {string | null | undefined} instanceId
 * @returns {Promise<'fi'|'en'|'sv'>}
 */
export async function resolveInstanceLanguage(pool, instanceId) {
  if (!pool || !instanceId) return 'fi';
  try {
    const r = await pool.query(
      'SELECT language, settings FROM kuutio_instances WHERE id = $1 LIMIT 1',
      [instanceId]
    );
    if (!r.rows.length) return 'fi';
    return getInstanceLanguageFromRow(r.rows[0]);
  } catch {
    return 'fi';
  }
}

/**
 * @param {'fi'|'en'|'sv'|string} lang
 * @returns {string}
 */
export function getResponseLanguageName(lang) {
  const m = {
    fi: 'Finnish',
    en: 'English',
    // BCP 47 sv-FI: Swedish as used in Finland (finlandssvenska), not rikssvenska
    sv: 'Finland Swedish (finlandssvenska, sv-FI)',
  };
  return m[normalizeInstanceLanguage(lang)] || 'Finnish';
}

/**
 * Variables merged into renderPrompt() for {{language}} substitution.
 * @param {'fi'|'en'|'sv'|string} lang
 * @returns {{ language: string }}
 */
export function getPromptLanguageVars(lang) {
  return { language: getResponseLanguageName(lang) };
}

/**
 * Prepended to the DB polish template system prompt so the model knows the target language.
 * @param {'fi'|'en'|'sv'|string} lang
 */
export function getLanguagePolishDirective(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return (
      'TARGET LANGUAGE: English.\n' +
      'If the input is not already natural English, translate it into clear English.\n' +
      'Then polish style and clarity for a civic workshop audience. Do not change meaning.'
    );
  }
  if (l === 'sv') {
    return (
      'TARGET LANGUAGE: Finland Swedish (finlandssvenska, sv-FI).\n' +
      'If the input is not already natural Finland Swedish, translate it into clear finlandssvenska.\n' +
      'Prefer Finnish-Swedish vocabulary and conventions over rikssvenska.\n' +
      'Then polish style and clarity for a civic workshop audience. Do not change meaning.'
    );
  }
  return (
    'TARGET LANGUAGE: Finnish.\n' +
    'If the input is not already natural Finnish, translate it into clear Finnish.\n' +
    'Then polish style and clarity for a civic workshop audience. Do not change meaning.'
  );
}

/** Short system line for generic Kuutio assistant fallbacks */
export function getAiFallbackSystemShort(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return "You are the Kuutio workshop AI assistant. Always respond in clear English unless the instructions say otherwise. Follow the user's task.";
  }
  if (l === 'sv') {
    return 'Du är Kuutio-workshopens AI-assistent. Svara alltid på tydlig finlandssvenska (sv-FI) om inte instruktionerna säger något annat. Följ användarens uppgift.';
  }
  return 'Olet Kuutio-työpajan AI-avustaja. Vastaa suomeksi. Noudata käyttäjän ohjeita.';
}

/** Chat-style: allow user language switch if they write in English */
export function getAiFallbackSystemFlexible(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return "You are the Kuutio workshop AI assistant. Respond in clear English unless the user writes in Finnish or Swedish and clearly expects that language. Use tools when the user asks about data.";
  }
  if (l === 'sv') {
    return 'Du är Kuutio-workshopens AI-assistent. Svara på tydlig finlandssvenska (sv-FI) om inte användaren tydligt skriver på annat språk och förväntar sig det. Använd verktyg när användaren frågar om data.';
  }
  return 'Olet Kuutio-työpajan AI-avustaja. Vastaa suomeksi, ellei käyttäjä kirjoita englanniksi. Käytä työkaluja kun käyttäjä kysyy datasta.';
}

export function getJsonStringsLanguageClause(lang) {
  const name = getResponseLanguageName(lang);
  return `JSON only; all human-readable string values must be in ${name}.`;
}

/**
 * Style header prepended to some generation prompts (PostIt AI, etc.).
 * @param {'fi'|'en'|'sv'|string} lang
 */
export function getDefaultLanguageStyleHeader(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return (
      'Kuutio workshop language note (tone and clarity; do not change meaning):\n' +
      '- Use clear, natural English appropriate for policymakers and citizens.\n' +
      '- Keep register professional but approachable.\n' +
      '- Do not use en/em dashes (–, —) to structure sentences; use commas, colons, or separate sentences instead. Use hyphens only where grammar requires (compound words), not as a stylistic separator.'
    );
  }
  if (l === 'sv') {
    return (
      'Kuutio-workshop språknotis (ton och tydlighet; ändra inte innebörden):\n' +
      '- Använd tydlig finlandssvenska (sv-FI) som passar beslutsfattare och medborgare i Finland.\n' +
      '- Föredra finlandssvenska uttryck framför rikssvenska när skillnaden är etablerad.\n' +
      '- Håll registret professionellt men tillgängligt.\n' +
      '- Använd inte tankstreck (–, —) för att strukturera meningar; använd komma, kolon eller separata meningar. Bindestreck endast när grammatiken kräver det, inte som stilgrepp.'
    );
  }
  return (
    'Kuutio-työpajan kieliohje (sävy ja selkeys; älä muuta merkitystä):\n' +
    '- Kirjoita selkeää, hyvää suomea. Säilytä sisältö ja tarkoitus ennallaan.\n' +
    '- Käytä yleiskieltä; vältä turhia englannismiä, kun suora suomennos on luonteva.\n' +
    '- Pidä rekisteri työpajaan sopivana: asiallinen, mutta ei tarpeettoman juhlallinen.\n' +
    '- Älä käytä ajatusviivoja (en/em-viiva: –, —) lauseiden jäsentämiseen; korvaa ne pilkulla, kaksoispisteellä tai erillisillä lauseilla. Käytä yhdysviivaa (-) vain kun kielioppi sitä vaatii (yhdyssanat), ei tyylikeinona.'
  );
}

const KUUTIO_LANGUAGE_LINE_FI = '- Kieli on aina suomi, ellei erikseen pyydetä muuta';

const KUUTIO_LANGUAGE_LINE_BY_LANG = {
  fi: KUUTIO_LANGUAGE_LINE_FI,
  en: '- Always respond in clear English unless explicitly asked otherwise',
  sv: '- Svara alltid på tydlig finlandssvenska (sv-FI) om inte annat uttryckligen begärs',
};

/**
 * Patch deprecated KUUTIO_BASE fallback string for response language.
 * @param {string} basePrompt
 * @param {'fi'|'en'|'sv'|string} lang
 */
export function injectResponseLanguageIntoKuutioBase(basePrompt, lang) {
  const l = normalizeInstanceLanguage(lang);
  const replacement = KUUTIO_LANGUAGE_LINE_BY_LANG[l] || KUUTIO_LANGUAGE_LINE_FI;
  if (!basePrompt || typeof basePrompt !== 'string') return basePrompt;
  if (basePrompt.includes(KUUTIO_LANGUAGE_LINE_FI)) {
    return basePrompt.replace(KUUTIO_LANGUAGE_LINE_FI, replacement);
  }
  return `${basePrompt}\n${replacement}`;
}

export function getLensPromptLabels(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return {
      obstacle: 'obstacle',
      opportunity: 'opportunity',
      obstacles: 'obstacles',
      opportunities: 'opportunities',
      obstacleGen: 'an obstacle',
      opportunityGen: 'an opportunity',
      obstacleCap: 'Obstacle',
      opportunityCap: 'Opportunity',
      itemListObstacle: 'OBSTACLE',
      itemListOpportunity: 'OPPORTUNITY',
      researchHeader: '\n\nRESEARCH SOURCES:\n',
      alreadyAddedHeader: '\n\nALREADY ADDED (do not repeat these):\n',
      sourceFallback: 'Source',
      unknownSource: 'Unknown source',
      obstacleInstruction:
        'What blocks or slows realization of this pivot from this lens perspective? What are concrete obstacles?',
      opportunityInstruction:
        'What enables or advances realization of this pivot from this lens perspective? What are concrete opportunities?',
    };
  }
  if (l === 'sv') {
    return {
      obstacle: 'hinder',
      opportunity: 'möjlighet',
      obstacles: 'hinder',
      opportunities: 'möjligheter',
      obstacleGen: 'ett hinder',
      opportunityGen: 'en möjlighet',
      obstacleCap: 'Hinder',
      opportunityCap: 'Möjlighet',
      itemListObstacle: 'HINDER',
      itemListOpportunity: 'MÖJLIGHET',
      researchHeader: '\n\nFORSKNINGSKÄLLOR:\n',
      alreadyAddedHeader: '\n\nREDAN TILLAGDA (upprepa inte dessa):\n',
      sourceFallback: 'Källa',
      unknownSource: 'Okänd källa',
      obstacleInstruction:
        'What blocks or slows realization of this pivot from this lens perspective? What are concrete obstacles?',
      opportunityInstruction:
        'What enables or advances realization of this pivot from this lens perspective? What are concrete opportunities?',
    };
  }
  return {
    obstacle: 'este',
    opportunity: 'mahdollisuus',
    obstacles: 'esteet',
    opportunities: 'mahdollisuudet',
    obstacleGen: 'esteen',
    opportunityGen: 'mahdollisuuden',
    obstacleCap: 'Este',
    opportunityCap: 'Mahdollisuus',
    itemListObstacle: 'ESTE',
    itemListOpportunity: 'MAHDOLLISUUS',
    researchHeader: '\n\nTUTKIMUSLÄHTEET:\n',
    alreadyAddedHeader: '\n\nJO LISÄTYT (älä toista näitä):\n',
    sourceFallback: 'Lähde',
    unknownSource: 'Tuntematon lähde',
    obstacleInstruction:
      'What blocks or slows realization of this pivot from this lens perspective? What are concrete obstacles?',
    opportunityInstruction:
      'What enables or advances realization of this pivot from this lens perspective? What are concrete opportunities?',
  };
}

/** Subject noun for evaluationTagsPromptRules (node / solution / item). */
export function getEvaluationTagSubject(kind, lang) {
  const l = normalizeInstanceLanguage(lang);
  const map = {
    fi: { node: 'solmu', solution: 'ratkaisu', item: 'kohde' },
    en: { node: 'node', solution: 'solution', item: 'item' },
    sv: { node: 'nod', solution: 'lösning', item: 'objekt' },
  };
  return (map[l] || map.fi)[kind] || (map[l] || map.fi).item;
}

/** User-facing pivot discovery progress / reason strings. */
export function getPivotDiscoveryUiStrings(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return {
      starting: 'Starting...',
      done: (count, duration) => `Done: ${count} pivot point(s) (${duration})`,
      doneZero: (duration, reason) => `Done: 0 pivot points (${duration})${reason ? ` — ${reason}` : ''}`,
      cancelled: 'Cancelled',
      error: (msg) => `Error: ${msg}`,
      interrupted: 'Analysis interrupted by a service restart — please start again.',
      analyzing: (n) => `AI is analyzing ${n} nodes — this takes 1–3 minutes...`,
      aiResponse: (s) => `AI response received (${s}s) — processing results...`,
      tooFewNodes: (n) => `Too few nodes for analysis (${n}/4).`,
      noPivotsParticipants: (distinct, min, effective) =>
        `No pivot points: only ${distinct} participant(s) in the data, threshold was ${min} (used ${effective}).`,
      noThemes: 'AI found no recurring themes in the nodes.',
      themesBelowThreshold: 'AI found themes, but none crossed the thresholds.',
    };
  }
  if (l === 'sv') {
    return {
      starting: 'Startar...',
      done: (count, duration) => `Klar: ${count} hävstångspunkt(er) (${duration})`,
      doneZero: (duration, reason) => `Klar: 0 hävstångspunkter (${duration})${reason ? ` — ${reason}` : ''}`,
      cancelled: 'Avbruten',
      error: (msg) => `Fel: ${msg}`,
      interrupted: 'Analysen avbröts av en omstart av tjänsten — starta igen.',
      analyzing: (n) => `AI analyserar ${n} noder — det tar 1–3 minuter...`,
      aiResponse: (s) => `AI-svar mottaget (${s}s) — bearbetar resultat...`,
      tooFewNodes: (n) => `För få noder för analys (${n}/4).`,
      noPivotsParticipants: (distinct, min, effective) =>
        `Inga hävstångspunkter: endast ${distinct} deltagare i datan, tröskeln var ${min} (använd ${effective}).`,
      noThemes: 'AI hittade inga återkommande teman i noderna.',
      themesBelowThreshold: 'AI hittade teman, men ingen nådde tröskelvärdena.',
    };
  }
  return {
    starting: 'Käynnistetään...',
    done: (count, duration) => `Valmis: ${count} vipupistettä (${duration})`,
    doneZero: (duration, reason) => `Valmis: 0 vipupistettä (${duration})${reason ? ` — ${reason}` : ''}`,
    cancelled: 'Peruutettu',
    error: (msg) => `Virhe: ${msg}`,
    interrupted: 'Analyysi keskeytyi palvelun uudelleenkäynnistykseen — käynnistä uudelleen.',
    analyzing: (n) => `AI analysoi ${n} solmua — tämä kestää 1–3 minuuttia...`,
    aiResponse: (s) => `AI vastaus saatu (${s}s) — käsitellään tuloksia...`,
    tooFewNodes: (n) => `Liian vähän solmuja analyysiin (${n}/4).`,
    noPivotsParticipants: (distinct, min, effective) =>
      `Ei vipupisteitä: datassa vain ${distinct} osallistuja(a), kynnys oli ${min} (käytetty ${effective}).`,
    noThemes: 'AI ei löytänyt toistuvia teemoja solmuista.',
    themesBelowThreshold: 'AI löysi teemoja, mutta yksikään ei ylittänyt kynnysarvoja.',
  };
}

/** Field / section headers for solution + org AI context blocks. */
export function getSolutionContextLabels(lang) {
  const l = normalizeInstanceLanguage(lang);
  if (l === 'en') {
    return {
      solution: 'SOLUTION',
      description: 'DESCRIPTION',
      problem: 'PROBLEM SOLVED',
      resources: 'RESOURCES AND ACTORS',
      whyFuture: 'WHY THIS BUILDS A BETTER FUTURE',
      impactMindsets: 'IMPACT ON MINDSETS',
      impactStructures: 'IMPACT ON STRUCTURES',
      impactBehavior: 'IMPACT ON BEHAVIOR',
      lensCoverage: 'LENS COVERAGE',
      covers: (n) => `SOLUTION COVERS (${n} items):`,
      coversDefault: 'covers',
      answeredBlockers: (n) => `OBSTACLES THE GROUP ANSWERED (${n}):`,
      openBlockers: (n) => `OPEN OBSTACLES (${n}):`,
      openBlockersDetail: (n) => `OPEN OBSTACLES (${n}) — the group has not addressed these:`,
      groupSolution: 'Group response',
      assessAnswer: '→ Assess: is the group response adequate for this obstacle?',
      noAnswer: '— the group has not answered this obstacle',
      summary: (a, b, o) =>
        `Summary: the solution directly covers ${a} items, the group answered ${b} obstacle(s), ${o} obstacle(s) remain fully open.`,
      lens: (name) => `Lens "${name}":`,
      lensEmpty: (name) => `Lens "${name}": (no identified obstacles or opportunities)`,
      obstacle: 'Obstacle',
      opportunity: 'Opportunity',
      focusAreas: 'Focus areas',
      strengths: 'Strengths',
      targets: 'Target groups',
      workshopPresence: 'Workshop representation',
      bestAt: 'Best at',
      typicalRole: 'Typical role',
      leverage: 'Leverage',
      usualPartners: 'Usual partners',
      gaps: 'Constraints',
      recentImpact: 'Recent impact',
      solutionsHeader: 'SOLUTIONS',
      orgsHeader: 'ORGANISATIONS',
      createActionsFallback: 'Create 3 actions per organisation.',
      hybridMarker: ' [HYBRID]',
      leadOrgs: 'Lead organisations',
      descShort: 'Description',
      problemShort: 'Problem',
      resourcesShort: 'Resources',
      group: 'Group',
      relevance: 'relevance',
      researchSources: 'RESEARCH SOURCES',
      unknownSource: 'Unknown source',
      pathContext: 'Path context',
      nodeLabel: 'Node',
      matrixPivot: 'Pivot',
      matrixTotal: 'Total',
      matrixObstacles: 'Obstacles',
      matrixOpportunities: 'Opportunities',
      evalTagsPrev: 'AI TAGS (from prior analysis):',
      tagFallbackUncharted: 'No direct research evidence was found — this may be uncharted territory.',
      tagFallbackCreative: 'The content brings new perspectives to the workshop context.',
      tagFallbackFeasible: 'Details can be refined in the workshop.',
      tagFallbackCreativeSol: 'The solution brings new perspectives to the workshop context.',
      tagFallbackAmbitiousSol: 'The solution has potential for broad impact.',
      tagFallbackFeasibleSol: 'Implementation details can be refined in the workshop.',
      hybridCombines: (n) => `(combines: ${n} solutions)`,
    };
  }
  if (l === 'sv') {
    return {
      solution: 'LÖSNING',
      description: 'BESKRIVNING',
      problem: 'PROBLEM SOM LÖSES',
      resources: 'RESURSER OCH AKTÖRER',
      whyFuture: 'VARFÖR DETTA BYGGER EN BÄTTRE FRAMTID',
      impactMindsets: 'PÅVERKAN PÅ TANKESÄTT',
      impactStructures: 'PÅVERKAN PÅ STRUKTURER',
      impactBehavior: 'PÅVERKAN PÅ BETEENDE',
      lensCoverage: 'LINSTÄCKNING',
      covers: (n) => `LÖSNINGEN TÄCKER (${n} punkter):`,
      coversDefault: 'täcker',
      answeredBlockers: (n) => `HINDER SOM GRUPPEN SVARAT PÅ (${n}):`,
      openBlockers: (n) => `ÖPPNA HINDER (${n}):`,
      openBlockersDetail: (n) => `ÖPPNA HINDER (${n}) — gruppen har inte behandlat dessa:`,
      groupSolution: 'Gruppens svar',
      assessAnswer: '→ Bedöm: är gruppens svar tillräckligt för detta hinder?',
      noAnswer: '— gruppen har inte svarat på detta hinder',
      summary: (a, b, o) =>
        `Sammanfattning: lösningen täcker direkt ${a} punkter, gruppen har svarat på ${b} hinder, ${o} hinder är helt öppna.`,
      lens: (name) => `Lins "${name}":`,
      lensEmpty: (name) => `Lins "${name}": (inga identifierade hinder eller möjligheter)`,
      obstacle: 'Hinder',
      opportunity: 'Möjlighet',
      focusAreas: 'Fokusområden',
      strengths: 'Styrkor',
      targets: 'Målgrupper',
      workshopPresence: 'Representation i workshopen',
      bestAt: 'Starkast inom',
      typicalRole: 'Typisk roll',
      leverage: 'Hävstång',
      usualPartners: 'Vanliga partner',
      gaps: 'Begränsningar',
      recentImpact: 'Senaste genomslag',
      solutionsHeader: 'LÖSNINGAR',
      orgsHeader: 'ORGANISATIONER',
      createActionsFallback: 'Skapa 3 åtgärder per organisation.',
      hybridMarker: ' [HYBRID]',
      leadOrgs: 'Ansvariga organisationer',
      descShort: 'Beskrivning',
      problemShort: 'Problem',
      resourcesShort: 'Resurser',
      group: 'Grupp',
      relevance: 'relevans',
      researchSources: 'FORSKNINGSKÄLLOR',
      unknownSource: 'Okänd källa',
      pathContext: 'Banans kontext',
      nodeLabel: 'Nod',
      matrixPivot: 'Hävstångspunkt',
      matrixTotal: 'Totalt',
      matrixObstacles: 'Hinder',
      matrixOpportunities: 'Möjligheter',
      evalTagsPrev: 'AI-TAGGAR (från tidigare analys):',
      tagFallbackUncharted: 'Ingen direkt forskningsbelägg hittades — idén kan vara okänd terräng.',
      tagFallbackCreative: 'Innehållet tillför nya perspektiv till workshopkontexten.',
      tagFallbackFeasible: 'Detaljer kan preciseras i workshopen.',
      tagFallbackCreativeSol: 'Lösningen tillför nya perspektiv till workshopkontexten.',
      tagFallbackAmbitiousSol: 'Lösningen har potential att påverka brett.',
      tagFallbackFeasibleSol: 'Genomförandedetaljer kan preciseras i workshopen.',
      hybridCombines: (n) => `(kombinerar: ${n} lösningar)`,
    };
  }
  return {
    solution: 'RATKAISU',
    description: 'KUVAUS',
    problem: 'ONGELMA JOKA RATKEAA',
    resources: 'RESURSSIT JA TOIMIJAT',
    whyFuture: 'MIKSI RAKENTAA PAREMPAA TULEVAISUUTTA',
    impactMindsets: 'VAIKUTUS AJATTELUMALLEIHIN',
    impactStructures: 'VAIKUTUS RAKENTEISIIN',
    impactBehavior: 'VAIKUTUS KÄYTTÄYTYMISEEN',
    lensCoverage: 'LINSSIKATTAVUUS',
    covers: (n) => `RATKAISU KATTAA (${n} kohtaa):`,
    coversDefault: 'kattaa',
    answeredBlockers: (n) => `ESTEET JOIHIN RYHMÄ ON VASTANNUT (${n}):`,
    openBlockers: (n) => `AVOIMET ESTEET (${n}):`,
    openBlockersDetail: (n) => `AVOIMET ESTEET (${n}) — ryhmä ei ole käsitellyt näitä:`,
    groupSolution: 'Ryhmän ratkaisu',
    assessAnswer: '→ Arvioi: onko ryhmän vastaus riittävä tähän esteeseen?',
    noAnswer: '— ryhmä ei ole vastannut tähän esteeseen',
    summary: (a, b, o) =>
      `Yhteenveto: ratkaisu kattaa suoraan ${a} kohtaa, ryhmä on vastannut ${b} esteeseen, ${o} estettä on täysin avoimena.`,
    lens: (name) => `Linssi "${name}":`,
    lensEmpty: (name) => `Linssi "${name}": (ei tunnistettuja esteitä tai mahdollisuuksia)`,
    obstacle: 'Este',
    opportunity: 'Mahdollisuus',
    focusAreas: 'Fokusalueet',
    strengths: 'Vahvuudet',
    targets: 'Kohderyhmät',
    workshopPresence: 'Edustus työpajassa',
    bestAt: 'Vahvimmillaan',
    typicalRole: 'Tyypillinen rooli',
    leverage: 'Vipuvoima',
    usualPartners: 'Tavanomaiset kumppanit',
    gaps: 'Rajoitteet',
    recentImpact: 'Viimeaikainen vaikuttavuus',
    solutionsHeader: 'RATKAISUT',
    orgsHeader: 'ORGANISAATIOT',
    createActionsFallback: 'Luo 3 toimenpidettä per organisaatio.',
    hybridMarker: ' [YHDISTELMÄ]',
    leadOrgs: 'Vastuuorganisaatiot',
    descShort: 'Kuvaus',
    problemShort: 'Ongelma',
    resourcesShort: 'Resurssit',
    group: 'Ryhmä',
    relevance: 'relevanssi',
    researchSources: 'TUTKIMUSLÄHTEET',
    unknownSource: 'Tuntematon lähde',
    pathContext: 'Polun konteksti',
    nodeLabel: 'Solmu',
    matrixPivot: 'Vipupiste',
    matrixTotal: 'Yhteensä',
    matrixObstacles: 'Esteet',
    matrixOpportunities: 'Mahdollisuudet',
    evalTagsPrev: 'AI-TUNNISTEET (aiemmassa analyysissä):',
    tagFallbackUncharted: 'Aiheesta ei löytynyt suoraa tutkimusnäyttöä — idea voi olla uutta maastoa.',
    tagFallbackCreative: 'Sisältö tuo uusia näkökulmia työpajan kontekstiin.',
    tagFallbackFeasible: 'Yksityiskohdat voidaan tarkentaa työpajassa.',
    tagFallbackCreativeSol: 'Ratkaisu tuo uusia näkökulmia työpajan kontekstiin.',
    tagFallbackAmbitiousSol: 'Ratkaisulla on potentiaalia vaikuttaa laajasti.',
    tagFallbackFeasibleSol: 'Toteutuksen yksityiskohdat voidaan tarkentaa työpajassa.',
    hybridCombines: (n) => `(yhdistää: ${n} ratkaisua)`,
  };
}

